import { DateFormats, formatDate, useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane, SeenIndicator } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { StatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import { type ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';

import { AppRoutes } from 'services/routes';
import { type MappedConversation, MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import {
    useGetConversationFilterCategoriesQuery,
    useGetConversationsQuery,
} from 'store/api/userSupportApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<MappedConversation>
>['sortProperties'] = [{ id: 'lastUpdated', desc: true }];

const columns: ColumnDef<MappedConversation>[] = [
    {
        accessorKey: 'isUnread',
        enableSorting: false,
        header: '',
        size: 1,
        cell: ({ getValue }) => (
            <SeenIndicator seen={!getValue()} size={SeenIndicator.sizes.LARGE} />
        ),
    },
    {
        accessorKey: 'subject',
        enableSorting: false,
        header: 'Subject',
        size: 180,
        cell: ({ getValue }) => (
            <div
                className={classNames({
                    'sds_messages--unread': getValue()?.isUnread,
                })}
            >
                <PrimaryData data={getValue()?.title} />
            </div>
        ),
    },
    {
        accessorKey: 'lastMessage',
        enableSorting: false,
        header: 'Last Message',
        size: 180,
    },
    {
        accessorKey: 'memberFullName',
        enableSorting: false,
        header: 'Patient',
        size: 100,
    },
    {
        accessorKey: 'createdBy',
        enableSorting: false,
        header: 'Created By',
        size: 70,
    },
    {
        accessorKey: 'lastUpdated',
        enableSorting: true,
        header: 'Last Updated',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.TIME_DATE_SHORT)} />
        ),
    },
    {
        accessorKey: 'conversationStatus',
        enableSorting: false,
        header: 'Conversation Status',
        size: 80,
        cell: ({ getValue }) => (
            <StatusIndicator
                message={getValue()}
                status={
                    (
                        {
                            [ChatWithConversationPane.ConversationStatus.OPEN]:
                                StatusIndicator.Status.SUCCESS,
                            [ChatWithConversationPane.ConversationStatus.CLOSED]:
                                StatusIndicator.Status.DEFAULT,
                        } as Record<
                            (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus],
                            (typeof StatusIndicator.Status)[keyof typeof StatusIndicator.Status]
                        >
                    )[getValue()]
                }
            />
        ),
    },
];

export const AllMessages = () => {
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();
    const debounceSearch = useDebouncedFunction();

    const { data: conversationFilterCategories } = useGetConversationFilterCategoriesQuery();

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.CONVERSATIONS, initialSortProperties);

    // The search box fires on every keystroke, so the request is driven by a lagging copy of the
    // search text. `search` itself stays current to keep the input responsive.
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const apiQueryParams = { page, searchText: debouncedSearch, activeFilters, sort };
    // The total count is read off page one so paging doesn't blank out the pager mid-request.
    // On page one this is the same cache entry as the query below, so it costs no extra request.
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetConversationsQuery({
        ...apiQueryParams,
        page: 0,
    });
    const { data: conversationData, isFetching: isCurrentPageDataFetching } =
        useGetConversationsQuery(apiQueryParams);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="All Messages"
                secondaryTitle={loggedInSupplierName}
                data={conversationData?.conversations}
                isLoading={
                    isFirstPageDataFetching ||
                    isCurrentPageDataFetching ||
                    search !== debouncedSearch
                }
                enableSorting
                manualSorting
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={(searchText) => {
                    updateGridSearch?.(searchText);
                    debounceSearch(() => setDebouncedSearch(searchText));
                }}
                filterCategories={
                    // Since grid is filtered on backend, data & filters don't have to match
                    conversationFilterCategories as FilterCategory<MappedConversation>[]
                }
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                totalCount={firstPageData?.count}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(rowData.referralId),
                            hash: MESSAGES_LOCATION_HASH,
                        },
                        {
                            state: {
                                conversationId: rowData.conversationId,
                                backToDestination: {
                                    text: 'Back to All Messages',
                                    route: AppRoutes.allMessages,
                                },
                            },
                        },
                    );
                }}
                columns={columns}
            />
        </FormPageContainer>
    );
};
